import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { PrivateReplyEligibilityDto } from "@multi-chat/shared";
import {
  ChevronDown as ChevronDownIcon,
  MessageCircle as PublicIcon,
  MessageSquareLock as PrivateIcon,
  SendHorizontal as SendIcon,
} from "lucide-react";
import { Button } from "../../../components/ui/button/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu/index.js";
import { cn } from "../../../lib/utils.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { formatConversationFullDate } from "../conversation-time.js";

export type CommentReplyMode = "public" | "private";
export type CommentSendPolicy = "send_and_handle" | "send_only";

const PUBLIC_MAX = 2200;
const PRIVATE_MAX = 1000;

export type CommentReplySendOptions = {
  markHandled: boolean;
};

export function CommentReplyComposer({
  privateReplyEligibility,
  onSendPublic,
  onSendPrivate,
  disabled = false,
  sendPolicy,
  onSendPolicyChange,
}: {
  privateReplyEligibility: PrivateReplyEligibilityDto;
  onSendPublic?: (content: string, options: CommentReplySendOptions) => Promise<boolean> | boolean;
  onSendPrivate?: (content: string, options: CommentReplySendOptions) => Promise<boolean> | boolean;
  disabled?: boolean;
  /** Page-session remembered policy; parent owns persistence. */
  sendPolicy: CommentSendPolicy;
  onSendPolicyChange: (policy: CommentSendPolicy) => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<CommentReplyMode>("public");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  const maxLength = mode === "public" ? PUBLIC_MAX : PRIVATE_MAX;
  const privateAllowed = privateReplyEligibility.allowed;
  const sendHandler = mode === "public" ? onSendPublic : onSendPrivate;
  const markHandled = sendPolicy === "send_and_handle";
  const primaryLabel =
    sendPolicy === "send_and_handle"
      ? t("inbox.comments.composer.sendAndHandle")
      : t("inbox.comments.composer.sendOnly");
  const canSend =
    !disabled &&
    !sending &&
    content.trim().length > 0 &&
    content.trim().length <= maxLength &&
    sendHandler != null &&
    (mode === "public" || privateAllowed);

  async function handleSend(): Promise<void> {
    if (!canSend || sendHandler == null) return;
    setSending(true);
    try {
      const ok = await sendHandler(content.trim(), { markHandled });
      if (ok) setContent("");
    } finally {
      setSending(false);
    }
  }

  const helpText =
    mode === "public"
      ? markHandled
        ? t("inbox.comments.composer.publicHelp")
        : t("inbox.comments.composer.publicHelpSendOnly")
      : privateAllowed
        ? markHandled
          ? t("inbox.comments.composer.privateHelp", {
              expires: privateReplyEligibility.expiresAt
                ? formatConversationFullDate(privateReplyEligibility.expiresAt, i18n.language)
                : t("inbox.comments.composer.expiresUnknown"),
            })
          : t("inbox.comments.composer.privateHelpSendOnly", {
              expires: privateReplyEligibility.expiresAt
                ? formatConversationFullDate(privateReplyEligibility.expiresAt, i18n.language)
                : t("inbox.comments.composer.expiresUnknown"),
            })
        : t(`inbox.comments.composer.privateBlocked.${privateReplyEligibility.reasonCode}`);

  return (
    <div className="shrink-0 border-t border-line/70 bg-surface px-3 py-2.5">
      <div
        className="mb-2 inline-flex rounded-lg bg-surface-muted p-0.5"
        role="tablist"
        aria-label={t("inbox.comments.composer.modeLabel")}
      >
        <ComposerModeTab
          active={mode === "public"}
          label={t("inbox.comments.composer.public")}
          hint={t("inbox.comments.composer.publicHint")}
          icon={<PublicIcon size={13} aria-hidden="true" />}
          onClick={() => setMode("public")}
        />
        <ComposerModeTab
          active={mode === "private"}
          label={t("inbox.comments.composer.private")}
          hint={t("inbox.comments.composer.privateHint")}
          icon={<PrivateIcon size={13} aria-hidden="true" />}
          onClick={() => setMode("private")}
        />
      </div>

      <p className="m-0 mb-2 text-[0.75rem] leading-snug text-muted">{helpText}</p>

      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <textarea
            value={content}
            onChange={(event) => setContent(event.target.value.slice(0, maxLength))}
            rows={2}
            disabled={disabled || (mode === "private" && !privateAllowed)}
            className={cn(
              "min-h-[40px] w-full resize-none rounded-xl border bg-canvas px-3 py-2 text-[0.84375rem] text-ink outline-none placeholder:text-muted focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-60",
              mode === "private"
                ? "border-warning/35 focus-visible:border-warning"
                : "border-line focus-visible:border-primary",
            )}
            placeholder={
              mode === "public"
                ? t("inbox.comments.composer.publicPlaceholder")
                : t("inbox.comments.composer.privatePlaceholder")
            }
            aria-label={
              mode === "public"
                ? t("inbox.comments.composer.public")
                : t("inbox.comments.composer.private")
            }
          />
          <div className="mt-1 flex justify-end text-[0.6875rem] tabular-nums text-muted">
            <LtrValue>
              {content.length}/{maxLength}
            </LtrValue>
          </div>
        </div>

        <div
          className={cn(
            "inline-flex shrink-0 overflow-hidden rounded-md border",
            mode === "private" ? "border-border-strong" : "border-primary",
          )}
        >
          <Button
            type="button"
            size="sm"
            variant={mode === "private" ? "outline" : "default"}
            disabled={!canSend}
            className="rounded-none border-0"
            onClick={() => {
              void handleSend();
            }}
            aria-label={primaryLabel}
            title={
              sendHandler == null ? t("inbox.comments.composer.sendUnavailable") : primaryLabel
            }
          >
            <SendIcon size={16} aria-hidden="true" />
            <span className="ms-1.5 hidden max-w-[9.5rem] truncate sm:inline">{primaryLabel}</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant={mode === "private" ? "outline" : "default"}
                disabled={disabled || sending}
                className={cn(
                  "rounded-none border-0 border-s px-2",
                  mode === "private" ? "border-s-border-strong" : "border-s-primary-hover/40",
                )}
                aria-label={t("inbox.comments.composer.sendPolicyMenu")}
              >
                <ChevronDownIcon size={14} aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[14rem]">
              <DropdownMenuItem
                onSelect={() => onSendPolicyChange("send_and_handle")}
                className={cn(sendPolicy === "send_and_handle" && "font-semibold text-primary")}
              >
                {t("inbox.comments.composer.sendAndHandle")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => onSendPolicyChange("send_only")}
                className={cn(sendPolicy === "send_only" && "font-semibold text-primary")}
              >
                {t("inbox.comments.composer.sendOnly")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}

function ComposerModeTab({
  active,
  label,
  hint,
  icon,
  onClick,
}: {
  active: boolean;
  label: string;
  hint: string;
  icon: React.ReactNode;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      title={hint}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[0.75rem] font-semibold transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
        active ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink",
      )}
      onClick={onClick}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
